import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useRazorpay } from "react-razorpay";
import { API, useAuth } from "../App";
import DashboardLayout from "../components/DashboardLayout";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { 
  Check, 
  Loader2, 
  CreditCard,
  Sparkles,
  Crown,
  Calendar,
  TrendingUp,
  Package,
  Image,
  Video,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Clock,
  ArrowUpRight,
  Receipt,
  Download
} from "lucide-react";

const PLANS = {
  starter: { 
    name: "Starter", 
    price: 999, 
    sku_limit: 50, 
    image_limit: 200, 
    video_limit: 50,
    features: ["50 SKU Credits", "200 AI Images", "50 Videos", "All Categories", "Email Support"]
  },
  pro: { 
    name: "Pro", 
    price: 2499, 
    sku_limit: 200, 
    image_limit: 800, 
    video_limit: 200,
    features: ["200 SKU Credits", "800 AI Images", "200 Videos", "Priority Support", "Team Access"]
  },
  studio: { 
    name: "Studio", 
    price: 4999, 
    sku_limit: 500, 
    image_limit: 2000, 
    video_limit: 500,
    features: ["500 SKU Credits", "2000 AI Images", "500 Videos", "Dedicated Support", "API Access"]
  }
};

export default function SubscriptionPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { error, isLoading, Razorpay } = useRazorpay();
  
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [payments, setPayments] = useState([]);
  const [upgrading, setUpgrading] = useState(null);

  useEffect(() => {
    fetchSubscription();
    fetchPayments();
  }, []);

  const fetchSubscription = async () => {
    try {
      const response = await axios.get(`${API}/user/subscription`, { withCredentials: true });
      setSubscription(response.data);
    } catch (error) {
      console.error("Failed to fetch subscription");
    } finally {
      setLoading(false);
    }
  };

  const fetchPayments = async () => {
    try {
      const response = await axios.get(`${API}/payments/history`, { withCredentials: true });
      setPayments(response.data);
    } catch (error) {
      console.error("Failed to fetch payments");
    }
  };

  const handleUpgrade = async (planId) => {
    if (isLoading || !Razorpay) {
      toast.error("Payment system is loading...");
      return;
    }

    setUpgrading(planId);

    try {
      const orderResponse = await axios.post(`${API}/payments/create-order`, {
        plan_id: planId,
        amount: PLANS[planId]?.price * 100 || 0,
        currency: "INR"
      }, { withCredentials: true });

      const { order_id, amount, currency, key_id, plan_name, user_name, user_email } = orderResponse.data;

      const options = {
        key: key_id,
        amount: amount,
        currency: currency,
        name: "Jini Studio AI",
        description: `${plan_name} Plan Subscription`,
        order_id: order_id,
        handler: async (response) => {
          try {
            await axios.post(`${API}/payments/verify`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              plan_id: planId
            }, { withCredentials: true });

            toast.success(`Successfully upgraded to ${plan_name} plan!`);
            fetchSubscription();
            fetchPayments();
          } catch (error) {
            toast.error("Payment verification failed");
          }
        },
        prefill: {
          name: user_name,
          email: user_email,
        },
        theme: { color: "#4338ca" },
        modal: {
          ondismiss: () => setUpgrading(null)
        }
      };

      const rzp = new Razorpay(options);
      rzp.on("payment.failed", (response) => {
        toast.error(`Payment failed: ${response.error.description}`);
        setUpgrading(null);
      });
      rzp.open();

    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create order");
      setUpgrading(null);
    }
  };

  const getStatusBadge = (status) => {
    const configs = {
      active: { icon: CheckCircle2, color: "bg-green-100 text-green-700", label: "Active" },
      expired: { icon: XCircle, color: "bg-red-100 text-red-700", label: "Expired" },
      cancelled: { icon: XCircle, color: "bg-gray-100 text-gray-700", label: "Cancelled" },
      created: { icon: Clock, color: "bg-yellow-100 text-yellow-700", label: "Pending" },
      paid: { icon: CheckCircle2, color: "bg-green-100 text-green-700", label: "Paid" },
      failed: { icon: XCircle, color: "bg-red-100 text-red-700", label: "Failed" }
    };
    const config = configs[status] || configs.created;
    return (
      <Badge className={`${config.color} flex items-center gap-1`}>
        <config.icon className="w-3 h-3" />
        {config.label}
      </Badge>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric"
    });
  };

  const currentPlan = subscription?.plan_id ? PLANS[subscription.plan_id] : null;
  const skuUsage = subscription ? (subscription.skus_used / subscription.sku_limit * 100) : 0;
  const imageUsage = subscription ? (subscription.images_used / subscription.image_limit * 100) : 0;
  const videoUsage = subscription ? (subscription.videos_used / subscription.video_limit * 100) : 0;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex justify-center items-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-bold">Subscription & Billing</h1>
            <p className="text-gray-600">Manage your plan and view payment history</p>
          </div>
          {!subscription && (
            <Button onClick={() => navigate("/pricing")} className="bg-indigo-600 hover:bg-indigo-700">
              <Sparkles className="w-4 h-4 mr-2" />
              Get Started
            </Button>
          )}
        </div>

        {/* Current Plan Card */}
        {subscription ? (
          <Card className="border-2 border-indigo-200 bg-gradient-to-br from-indigo-50 to-white">
            <CardHeader>
              <div className="flex justify-between items-start">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Crown className="w-5 h-5 text-indigo-600" />
                    <CardTitle className="text-xl">{currentPlan?.name || "Free"} Plan</CardTitle>
                    {getStatusBadge(subscription.status)}
                  </div>
                  <CardDescription>
                    Valid until {formatDate(subscription.valid_until)}
                  </CardDescription>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-indigo-600">
                    ₹{currentPlan?.price?.toLocaleString() || 0}
                  </p>
                  <p className="text-sm text-gray-500">/month</p>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Usage Stats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white rounded-lg p-4 border">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Package className="w-4 h-4 text-indigo-600" />
                      <span className="text-sm font-medium">SKU Credits</span>
                    </div>
                    <span className="text-sm text-gray-500">
                      {subscription.skus_used}/{subscription.sku_limit}
                    </span>
                  </div>
                  <Progress value={skuUsage} className="h-2" />
                  <p className="text-xs text-gray-500 mt-1">
                    {subscription.sku_limit - subscription.skus_used} remaining
                  </p>
                </div>

                <div className="bg-white rounded-lg p-4 border">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Image className="w-4 h-4 text-green-600" />
                      <span className="text-sm font-medium">AI Images</span>
                    </div>
                    <span className="text-sm text-gray-500">
                      {subscription.images_used}/{subscription.image_limit}
                    </span>
                  </div>
                  <Progress value={imageUsage} className="h-2" />
                  <p className="text-xs text-gray-500 mt-1">
                    {subscription.image_limit - subscription.images_used} remaining
                  </p>
                </div>

                <div className="bg-white rounded-lg p-4 border">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Video className="w-4 h-4 text-purple-600" />
                      <span className="text-sm font-medium">Videos</span>
                    </div>
                    <span className="text-sm text-gray-500">
                      {subscription.videos_used}/{subscription.video_limit}
                    </span>
                  </div>
                  <Progress value={videoUsage} className="h-2" />
                  <p className="text-xs text-gray-500 mt-1">
                    {subscription.video_limit - subscription.videos_used} remaining
                  </p>
                </div>
              </div>

              {/* Plan Features */}
              <div className="bg-white rounded-lg p-4 border">
                <h4 className="font-medium mb-3">Plan Features</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {currentPlan?.features?.map((feature, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm text-gray-600">
                      <Check className="w-4 h-4 text-green-600" />
                      {feature}
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-2 border-dashed border-gray-300">
            <CardContent className="py-12 text-center">
              <AlertCircle className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium mb-2">No Active Subscription</h3>
              <p className="text-gray-500 mb-4">
                Subscribe to a plan to start creating AI photoshoots
              </p>
              <Button onClick={() => navigate("/pricing")}>
                View Plans
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Upgrade Options */}
        {subscription && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <TrendingUp className="w-5 h-5" />
                Upgrade Your Plan
              </CardTitle>
              <CardDescription>
                Get more credits and features with a higher plan
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-3 gap-4">
                {Object.entries(PLANS).map(([planId, plan]) => {
                  const isCurrentPlan = subscription?.plan_id === planId;
                  const isDowngrade = plan.price < (currentPlan?.price || 0);
                  
                  return (
                    <div 
                      key={planId}
                      className={`
                        rounded-lg border-2 p-4 transition-all
                        ${isCurrentPlan ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200 hover:border-gray-300'}
                      `}
                    >
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h4 className="font-semibold">{plan.name}</h4>
                          <p className="text-lg font-bold">₹{plan.price.toLocaleString()}<span className="text-sm font-normal text-gray-500">/mo</span></p>
                        </div>
                        {isCurrentPlan && (
                          <Badge className="bg-indigo-600">Current</Badge>
                        )}
                      </div>
                      
                      <ul className="space-y-1 mb-4 text-sm text-gray-600">
                        <li>{plan.sku_limit} SKU Credits</li>
                        <li>{plan.image_limit} AI Images</li>
                        <li>{plan.video_limit} Videos</li>
                      </ul>
                      
                      <Button
                        variant={isCurrentPlan ? "outline" : "default"}
                        size="sm"
                        className="w-full"
                        disabled={isCurrentPlan || upgrading !== null}
                        onClick={() => handleUpgrade(planId)}
                      >
                        {upgrading === planId ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Processing...
                          </>
                        ) : isCurrentPlan ? (
                          "Current Plan"
                        ) : isDowngrade ? (
                          "Downgrade"
                        ) : (
                          <>
                            Upgrade
                            <ArrowUpRight className="w-4 h-4 ml-1" />
                          </>
                        )}
                      </Button>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Payment History */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Receipt className="w-5 h-5" />
              Payment History
            </CardTitle>
          </CardHeader>
          <CardContent>
            {payments.length > 0 ? (
              <div className="space-y-3">
                {payments.map((payment, i) => (
                  <div 
                    key={payment.payment_id || i}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                        <CreditCard className="w-5 h-5 text-indigo-600" />
                      </div>
                      <div>
                        <p className="font-medium">
                          {PLANS[payment.plan_id]?.name || payment.plan_id} Plan
                        </p>
                        <p className="text-sm text-gray-500">
                          {formatDate(payment.created_at)}
                          {payment.razorpay_payment_id && (
                            <span className="ml-2 text-xs text-gray-400">
                              ID: {payment.razorpay_payment_id.slice(-8)}
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="font-semibold">₹{(payment.amount / 100).toLocaleString()}</p>
                        {getStatusBadge(payment.status)}
                      </div>
                      {payment.status === "paid" && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            window.open(`${API}/payments/${payment.payment_id}/invoice`, '_blank');
                          }}
                          title="Download Invoice"
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Receipt className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                <p>No payment history yet</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
